import React from 'react';
import { SEO } from '@/components/SEO';
import ServiceSchema from '@/components/services/ServiceSchema';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import { Filter } from 'lucide-react';
import gutterCleaning1 from '@/assets/gutter-cleaning-1.webp';
import gutterCleaning2 from '@/assets/gutter-cleaning-2.webp';

const GutterCleaningPage = () => {
  const serviceData = {
    title: "Professional Gutter Cleaning in Ventura, CA",
    subtitle: "Protect Your Ventura County Home from Water Damage",
    description: "Keep your gutters flowing freely with our professional gutter cleaning services. We remove debris, inspect for damage, and ensure proper water flow to protect your Ventura County property investment.",
    icon: <Filter className="h-16 w-16 text-ventura-blue" />,
    beforeImage: gutterCleaning1,
    afterImage: gutterCleaning2
  };

  const benefits = [
    "Prevents costly water damage",
    "Extends gutter system lifespan",
    "Protects foundation and landscaping",
    "Professional safety equipment used",
    "Comprehensive debris removal",
    "Downspout cleaning and inspection"
  ];

  const faqs = [
    {
      question: "How often should I clean gutters in Ventura County?",
      answer: "Due to Ventura's oak trees and seasonal weather patterns, we recommend gutter cleaning twice yearly - once in late fall and once in early spring to handle leaf debris and winter runoff."
    },
    {
      question: "What happens if I don't clean my gutters regularly?",
      answer: "Clogged gutters can cause water overflow, leading to foundation damage, roof leaks, and landscape erosion - all costly problems in Ventura County's clay soil conditions."
    },
    {
      question: "Do you provide gutter services throughout Ventura County?",
      answer: "Yes! We offer professional gutter cleaning in Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura."
    }
  ];

  return (
    <>
      <SEO
        title="Gutter Cleaning Ventura CA | Professional Gutter Maintenance Services"
        description="Professional gutter cleaning Ventura & Oxnard CA. Prevent water damage with expert debris removal in Camarillo, Thousand Oaks. Free estimates!"
        keywords="gutter cleaning Ventura CA, gutter maintenance Camarillo, downspout cleaning Thousand Oaks, Ventura County gutter services, gutter cleaning experts Ventura County, emergency gutter cleaning storm season"
        path="/services/gutter-cleaning"
      />
      <ServiceSchema
        serviceName="Gutter Cleaning"
        description="Professional gutter cleaning Ventura & Oxnard CA. Prevent water damage with expert debris removal in Camarillo, Thousand Oaks."
        serviceType="Gutter Cleaning"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Pressure Washing", "Soft Wash", "Window Cleaning"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">How Often Should You Clean Gutters in Ventura County?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                We recommend gutter cleaning twice yearly — once in late fall and once in early spring — to handle leaf debris from
                Ventura's oak trees ahead of winter runoff. Skipping this can cause clogged gutters to overflow, leading to foundation
                damage and roof leaks, especially costly given the region's clay soil conditions.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Complete Gutter System Care</h3>
                  <p className="text-gray-600 mb-4">
                    Comprehensive gutter services for your Ventura County home:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Thorough debris removal</li>
                    <li>Downspout cleaning and flushing</li>
                    <li>Gutter inspection and minor repairs</li>
                    <li>Roof edge and fascia cleaning</li>
                    <li>Gutter guard installation</li>
                    <li>Seasonal maintenance programs</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Clogged gutters before professional cleaning in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Clean gutters after professional maintenance service" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Understanding Ventura County's Seasonal Gutter Challenges</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Fall Leaf Management</h4>
                    <p className="text-gray-600 mb-4">
                      Ventura County's beautiful oak trees and other deciduous vegetation create significant leaf debris in fall months. Areas like Thousand Oaks and Oak View are particularly affected, requiring thorough gutter cleaning to prevent overflow during winter rains.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Wind-Blown Debris Removal</h4>
                    <p className="text-gray-600 mb-4">
                      Our famous Santa Ana winds and ocean breezes carry debris from across the region into gutters. From agricultural chaff in Fillmore to coastal sand in Port Hueneme, we understand the unique cleaning challenges each area of Ventura County presents.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Preventing Water Damage in Ventura County</h3>
                <p className="text-gray-600 mb-4">
                  Ventura County's soil conditions vary dramatically from the sandy coastal areas to the clay-rich inland regions. Proper gutter function is crucial for directing water away from foundations, especially in areas like Camarillo and Simi Valley where expansive clay soils can shift dramatically with moisture changes.
                </p>
                <p className="text-gray-600 mb-4">
                  Our comprehensive gutter cleaning service includes careful inspection of water flow patterns and potential problem areas. We identify and address minor issues before they become costly repairs, protecting your investment in your Ventura County property.
                </p>
                <p className="text-gray-600 mb-6">
                  During intense winter storms, clogged gutters can cause water to back up under roof edges, leading to interior damage. This is particularly problematic for the tile roofs common throughout Ventura County, where water intrusion can cause extensive damage to underlying structures. As gutter cleaning experts serving all of Ventura County, we offer emergency gutter cleaning ahead of major storms so overflow doesn't turn into a repair bill.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-12">
                <h3 className="text-2xl font-semibold mb-4">Professional Safety and Thoroughness</h3>
                <p className="text-gray-600 mb-4">
                  Gutter cleaning can be dangerous work, especially on multi-story homes common in hillside areas of Ventura and Thousand Oaks. Our professional team uses proper safety equipment and techniques to access all areas of your gutter system safely, eliminating the risk of injury that comes with DIY gutter maintenance.
                </p>
                <p className="text-gray-600">
                  We don't just remove visible debris – our thorough process includes flushing downspouts to ensure proper drainage, checking for leaks or damage, and inspecting fascia boards for signs of water damage. This comprehensive approach ensures your gutter system functions optimally throughout Ventura County's varied weather conditions.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Gutter Guard Solutions for Reduced Maintenance</h3>
                <p className="text-gray-600 mb-4">
                  For homeowners in heavily wooded areas like Oak Park or properties with extensive landscaping in Moorpark, gutter guards can significantly reduce maintenance needs. We install high-quality gutter protection systems that allow water flow while blocking leaves and debris.
                </p>
                <p className="text-gray-600">
                  Our gutter guard installation service includes assessment of your property's specific debris patterns and recommendation of the most appropriate protection system. While no system eliminates the need for periodic maintenance entirely, quality gutter guards can extend the time between cleanings and reduce the severity of blockages.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Gutter Cleaning"
          relatedServices={[
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Soft Wash', path: '/services/soft-wash', icon: 'soft' },
            { name: 'Window Cleaning', path: '/services/window-washing', icon: 'window' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Gutter Cleaning" />
        <ServiceCTA 
          title="Ready to Protect Your Property?"
          description="Schedule your professional gutter cleaning and safeguard your Ventura County home from water damage."
        />
        <Footer />
      </div>
    </>
  );
};

export default GutterCleaningPage;